import { Button, Group, Modal, Stack } from '@mantine/core';
import { get } from 'lodash';
import moment from 'moment';
import { useEffect, useRef } from 'react';

import { EORIGIN_DATE_FORMAT, TransitDocumentsEnum } from '@e-origin/shared';

import { Select } from '@components';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { TransitDocumentFormFactory } from './TransitDocumentFormFactory';
import { FormDocumentsInputType, TransitDocumentFormSchema } from './TransitDocumentFormSchema';

interface DocDetailsProps {
  type: TransitDocumentsEnum;
  dataSource: any;
  dataSourceIndex: number;
}

interface TransitDocumentEditProps {
  opened: boolean;
  onHide: () => void;
  docDetails?: DocDetailsProps;
  editDisabled: boolean;
  disabledDocuments: TransitDocumentsEnum[];
  onSave: (savedDocument: any) => void;
  forGoodsItem?: boolean;
  isArrival: boolean;
}

export const documentsLabels = {
  [TransitDocumentsEnum.PREVIOUS_DOCUMENT]: 'Previous document',
  [TransitDocumentsEnum.SUPPORTING_DOCUMENT]: 'Supporting document',
  [TransitDocumentsEnum.TRANSPORT_DOCUMENT]: 'Transport document',
  [TransitDocumentsEnum.ADDITIONAL_REFERENCE]: 'Additional reference',
  [TransitDocumentsEnum.ADDITIONAL_INFORMATION]: 'Additional information',
};

export const TransitDocumentEdit: React.FC<TransitDocumentEditProps> = ({
  opened,
  onHide,
  docDetails,
  editDisabled = false,
  disabledDocuments = [],
  onSave,
  forGoodsItem,
  isArrival,
}) => {
  const documentsTypesList = useRef(
    Object.keys(documentsLabels)
      .filter((docType) => !disabledDocuments.includes(docType as TransitDocumentsEnum))
      .map((docType) => ({ value: docType, label: documentsLabels[docType], 'data-pw': `document-type-option-${docType}` })),
  );

  const buildInitialFieldValues = (type?: TransitDocumentsEnum, data?: any) => {
    if (!type) return {};

    const formSchema = TransitDocumentFormSchema[type];
    if (!formSchema?.fields) return {};

    const initialData = {};

    Object.keys(formSchema.fields).forEach((fieldPath) => {
      const fieldConfig = formSchema.fields[fieldPath];

      if (fieldConfig.type === FormDocumentsInputType.NUMBER) {
        initialData[fieldPath] = Number(get(data, fieldPath)) || undefined;
      } else if (fieldConfig.type === FormDocumentsInputType.DATE) {
        const dateValue = get(data, fieldPath);
        initialData[fieldPath] = dateValue ? moment(dateValue, EORIGIN_DATE_FORMAT).format(EORIGIN_DATE_FORMAT) : undefined;
      } else {
        initialData[fieldPath] = get(data, fieldPath) || '';
      }
    });

    return initialData;
  };

  const form = useFormWithNameInputProp({
    initialValues: {
      documentType: docDetails?.type || null,
      operation: docDetails?.dataSource?.operation || undefined,
      id: docDetails?.dataSource?.id || {},
      ...buildInitialFieldValues(docDetails?.type, docDetails?.dataSource),
    },
  });

  useEffect(() => {
    if (form.values.documentType) {
      const currentSchema = TransitDocumentFormSchema[form.values.documentType as TransitDocumentsEnum];
      if (currentSchema?.fields) {
        const newValues = buildInitialFieldValues(form.values.documentType as TransitDocumentsEnum, {});
        form.setValues({
          documentType: form.values.documentType,
          operation: form.values.operation,
          id: form.values.id,
          ...newValues,
        });
      }
    }
  }, [form.values.documentType]);

  useEffect(() => {
    form.setValues({
      documentType: docDetails?.type,
      operation: docDetails?.dataSource?.operation,
      id: docDetails?.dataSource?.id || {},
      ...buildInitialFieldValues(docDetails?.type, docDetails?.dataSource),
    });
  }, [docDetails]);

  const onSubmit = (values) => {
    if (!values.documentType) {
      return;
    }

    const formData = {
      type: values.documentType,
      operation: values.operation,
      docIndex: docDetails?.dataSourceIndex,
      data: {},
    };

    const currentSchema = TransitDocumentFormSchema[values.documentType as TransitDocumentsEnum];
    if (currentSchema?.fields) {
      const fieldData = {};
      Object.keys(currentSchema.fields).forEach((fieldPath) => {
        fieldData[fieldPath] = values[fieldPath];
      });
      formData.data = fieldData;
    }

    onSave(formData);
    onHide();
  };

  const isDropdownDisabled = editDisabled || (form.values.id && Object.keys(form.values.id).some((key) => !!form.values.id[key]));

  return (
    <Modal title={docDetails ? 'Edit document' : 'Add new document'} opened={opened} onClose={onHide} centered zIndex={9999}>
      <Stack>
        <Select
          {...form.getInputProps('documentType')}
          label="Type of document"
          data={documentsTypesList.current}
          value={form.values.documentType}
          disabled={isDropdownDisabled}
          data-pw="document-type-select"
        />

        <TransitDocumentFormFactory
          isArrival={isArrival}
          type={form.values.documentType as TransitDocumentsEnum}
          form={form}
          editDisabled={editDisabled}
          forGoodsItem={forGoodsItem}
        />
      </Stack>
      {!editDisabled && (
        <Group justify="center" mt="xl">
          <Button type="button" key="save-modal-btn" variant="filled" size="lg" onClick={() => onSubmit(form.values)} data-pw="save-transit-doc-btn">
            {docDetails ? 'Update' : 'Add'}
          </Button>
        </Group>
      )}
    </Modal>
  );
};
